export interface ParsedFeishuSheetUrl {
  spreadsheetToken: string;
  sheetId?: string;
}

export interface ParsedFeishuWikiUrl {
  wikiNodeToken: string;
  sheetId?: string;
}

function parseFeishuUrl(url: string): URL {
  let parsed: URL;
  try {
    parsed = new URL(url.trim());
  } catch {
    throw new Error("无法识别飞书表格链接，请输入完整的 URL");
  }
  return parsed;
}

function getSheetId(parsed: URL): string | undefined {
  return parsed.searchParams.get("sheet")?.trim() || undefined;
}

export function parseFeishuSheetUrl(url: string): ParsedFeishuSheetUrl {
  const parsed = parseFeishuUrl(url);

  const match = parsed.pathname.match(/\/sheets\/([a-zA-Z0-9_-]+)/);
  if (!match?.[1]) {
    throw new Error("无法从链接中解析 spreadsheetToken，请确认是飞书电子表格链接");
  }

  return {
    spreadsheetToken: match[1],
    sheetId: getSheetId(parsed),
  };
}

export function parseFeishuWikiUrl(url: string): ParsedFeishuWikiUrl {
  const parsed = parseFeishuUrl(url);
  const match = parsed.pathname.match(/\/wiki\/([a-zA-Z0-9_-]+)/);
  if (!match?.[1]) {
    throw new Error("无法从链接中解析 Wiki 节点 token，请确认是飞书知识库链接");
  }

  return {
    wikiNodeToken: match[1],
    sheetId: getSheetId(parsed),
  };
}

/** 从飞书表格或知识库表格链接中读取可选的 sheet 参数。 */
export function getFeishuSheetIdFromUrl(url: string): string | undefined {
  return getSheetId(parseFeishuUrl(url));
}

/** 将 1-based 列索引转为 Excel 列名，如 1→A, 27→AA */
export function columnIndexToLetter(index: number): string {
  if (!Number.isInteger(index) || index < 1) {
    throw new Error(`无效的列索引: ${index}`);
  }

  let result = "";
  let n = index;
  while (n > 0) {
    const remainder = (n - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    n = Math.floor((n - 1) / 26);
  }
  return result;
}

/** 飞书写入/样式 API 要求 range 为 sheetId!A1:B2；单格需写成 D8:D8 */
export function normalizeFeishuWriteRange(range: string): string {
  const trimmed = range.trim();
  if (/^[^!]+![^:]+:[^:]+$/.test(trimmed)) {
    return trimmed;
  }

  const singleCell = trimmed.match(/^([^!]+)!([A-Za-z]+)(\d+)$/);
  if (singleCell) {
    const [, sheetId, column, row] = singleCell;
    return `${sheetId}!${column}${row}:${column}${row}`;
  }

  return trimmed;
}
